import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Inbox, RefreshCw, Loader2, Download, FileText, AlertTriangle, CheckCircle2, Clock,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { api, formatMoney, formatDateTime } from '@/lib/api'

interface DFeDoc {
  id: number
  nsu: string
  tipo: string
  schema: string | null
  chave_acesso: string | null
  cnpj_emit: string | null
  nome_emit: string | null
  valor_total: number
  data_emissao: string | null
  situacao: string | null
  manifestado_em: string | null
  manifestacao_status: string | null
  tem_xml: boolean
  nf_entrada_id: number | null
  nf_status: string | null
}

interface Controle {
  ultimo_nsu: string | null
  max_nsu: string | null
  ultima_consulta_at: string | null
  ultimo_cstat: string | null
  ultimo_motivo: string | null
  total_documentos: number
  resumos: number
  completos: number
}

function fmtCnpj(c: string | null) {
  if (!c) return '—'
  const d = c.replace(/\D/g, '')
  if (d.length === 14) return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
  return c
}

export default function DFeEntradaPage() {
  const qc = useQueryClient()
  const [filtro, setFiltro] = useState<'pendentes' | 'completos' | 'todos'>('pendentes')

  const { data: ctrl } = useQuery<Controle>({
    queryKey: ['dfe-controle'],
    queryFn: async () => (await api.get('/compras/dfe/controle')).data,
    refetchInterval: 30000,
  })

  const { data: docs = [], isLoading } = useQuery<DFeDoc[]>({
    queryKey: ['dfe-docs', filtro],
    queryFn: async () => {
      const params: any = { limit: 500 }
      if (filtro === 'completos') params.tipo = 'completo'
      if (filtro === 'pendentes') params.pendentes = true
      return (await api.get('/compras/dfe', { params })).data
    },
    refetchInterval: 30000,
  })

  const sincronizar = useMutation({
    mutationFn: async () => (await api.post('/compras/dfe/sincronizar')).data,
    onSuccess: (r: any) => {
      if (r?.erro) {
        toast.error(r.erro)
      } else {
        toast.success(
          `SEFAZ: ${r.docs_novos || 0} novos, ${r.pre_entradas || 0} pré-entradas, ${r.manifestados || 0} manifestados`,
          { duration: 6000 }
        )
      }
      qc.invalidateQueries({ queryKey: ['dfe-docs'] })
      qc.invalidateQueries({ queryKey: ['dfe-controle'] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao sincronizar'),
  })

  async function baixarXml(d: DFeDoc) {
    try {
      const resp = await api.get(`/compras/dfe/${d.id}/xml`, { responseType: 'blob' })
      const url = URL.createObjectURL(resp.data)
      const a = document.createElement('a')
      a.href = url
      a.download = `${d.chave_acesso || d.nsu}.xml`
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      toast.error('XML ainda não disponível')
    }
  }

  const atrasado = ctrl && ctrl.max_nsu && ctrl.ultimo_nsu &&
    Number(ctrl.max_nsu) > Number(ctrl.ultimo_nsu)
  const faltam = atrasado ? Number(ctrl!.max_nsu) - Number(ctrl!.ultimo_nsu) : 0

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Inbox className="text-brand-600" /> NF-e a Receber (SEFAZ)
          </h1>
          <p className="text-ink-600 text-sm">
            Tudo que faturam contra o seu CNPJ, puxado direto da SEFAZ. Manifesta Ciência
            automática e gera a pré-entrada com o XML.
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <Link to="/compras/nf-entradas" className="btn-secondary text-sm">NFs de Entrada →</Link>
          <button onClick={() => sincronizar.mutate()} disabled={sincronizar.isPending}
            className="btn-primary text-sm flex items-center gap-2 disabled:opacity-50">
            {sincronizar.isPending ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />}
            Sincronizar agora
          </button>
        </div>
      </div>

      {/* Status do cursor */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="card card-body">
          <div className="text-xs text-ink-500">Documentos na caixa</div>
          <div className="text-2xl font-bold">{ctrl?.total_documentos ?? '—'}</div>
          <div className="text-[11px] text-ink-400">{ctrl?.resumos ?? 0} resumos · {ctrl?.completos ?? 0} c/ XML</div>
        </div>
        <div className="card card-body">
          <div className="text-xs text-ink-500">Última consulta SEFAZ</div>
          <div className="text-sm font-semibold">{ctrl?.ultima_consulta_at ? formatDateTime(ctrl.ultima_consulta_at) : 'nunca'}</div>
          <div className="text-[11px] text-ink-400">{ctrl?.ultimo_cstat ? `cStat ${ctrl.ultimo_cstat}` : ''}</div>
        </div>
        <div className="card card-body">
          <div className="text-xs text-ink-500">Posição (NSU)</div>
          <div className="text-sm font-semibold font-mono">{ctrl?.ultimo_nsu ? Number(ctrl.ultimo_nsu) : 0} / {ctrl?.max_nsu ? Number(ctrl.max_nsu) : 0}</div>
          <div className="text-[11px] text-ink-400">{atrasado ? `faltam ~${faltam} a baixar` : 'em dia'}</div>
        </div>
        <div className="card card-body">
          <div className="text-xs text-ink-500">Sincronização</div>
          <div className="text-sm font-semibold flex items-center gap-1">
            <Clock size={14} className="text-ink-400" /> automática (30min)
          </div>
          <div className="text-[11px] text-ink-400">Ciência liberada p/ NF ≤ 10 dias</div>
        </div>
      </div>

      {atrasado && (
        <div className="card card-body bg-amber-50 border-amber-200 flex items-center gap-3 text-sm text-amber-900">
          <AlertTriangle className="text-amber-600 shrink-0" size={18} />
          <span>
            Ainda baixando o histórico da SEFAZ (~{faltam} documentos). O processo roda em
            lotes pra respeitar o limite da SEFAZ — clique em <strong>Sincronizar agora</strong> pra adiantar.
          </span>
        </div>
      )}

      {/* Filtros */}
      <div className="flex items-center gap-2 text-sm">
        <span className="text-ink-600 text-xs">Mostrar:</span>
        {([
          ['pendentes', 'Pendentes'],
          ['completos', 'Com XML'],
          ['todos', 'Todos'],
        ] as const).map(([k, label]) => (
          <button key={k} onClick={() => setFiltro(k)}
            className={`px-3 py-1 rounded text-xs ${filtro === k ? 'bg-brand-600 text-white' : 'bg-ink-100 text-ink-700'}`}>
            {label}
          </button>
        ))}
      </div>

      {/* Tabela */}
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Emissão</th>
              <th>Fornecedor (emitente)</th>
              <th>CNPJ</th>
              <th className="text-right">Valor</th>
              <th>Documento</th>
              <th>Manifestação</th>
              <th>Pré-entrada</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={8} className="text-center py-6 text-ink-400">
                <Loader2 className="animate-spin inline" size={18} /> Carregando...
              </td></tr>
            )}
            {!isLoading && docs.length === 0 && (
              <tr><td colSpan={8} className="text-center py-8 text-ink-400">
                Nenhum documento. Clique em "Sincronizar agora" pra buscar na SEFAZ.
              </td></tr>
            )}
            {docs.map(d => (
              <tr key={d.id}>
                <td className="whitespace-nowrap text-xs">{d.data_emissao ? formatDateTime(d.data_emissao) : '—'}</td>
                <td className="text-sm">{d.nome_emit || '—'}</td>
                <td className="text-xs font-mono text-ink-500">{fmtCnpj(d.cnpj_emit)}</td>
                <td className="text-right font-semibold">{formatMoney(d.valor_total)}</td>
                <td>
                  {d.tipo === 'completo' ? (
                    <span className="px-2 py-0.5 rounded text-[11px] bg-emerald-100 text-emerald-800 flex items-center gap-1 w-fit">
                      <CheckCircle2 size={11} /> XML completo
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 rounded text-[11px] bg-ink-100 text-ink-600 w-fit inline-block">resumo</span>
                  )}
                </td>
                <td className="text-xs">
                  {d.manifestado_em ? (
                    <span className="text-ink-600" title={d.manifestacao_status || ''}>
                      {(d.manifestacao_status || '').startsWith('1') || (d.manifestacao_status || '').includes('573')
                        ? '✓ ciência' : (d.manifestacao_status || 'feita').slice(0, 28)}
                    </span>
                  ) : (
                    <span className="text-ink-400">aguardando</span>
                  )}
                </td>
                <td>
                  {d.nf_entrada_id ? (
                    <Link to="/compras/nf-entradas"
                      className="text-brand-600 hover:underline text-xs flex items-center gap-1">
                      <FileText size={12} /> #{d.nf_entrada_id} {d.nf_status ? `(${d.nf_status.replace('_', ' ')})` : ''}
                    </Link>
                  ) : (
                    <span className="text-ink-400 text-xs">—</span>
                  )}
                </td>
                <td>
                  {d.tem_xml && (
                    <button onClick={() => baixarXml(d)}
                      className="text-ink-500 hover:text-brand-600" title="Baixar XML">
                      <Download size={15} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
